import { LabelMode } from '@wix/add-panel-common';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import classnames from 'classnames';
import React, { FC, MouseEvent } from 'react';
import { callOrUse } from '../../../../../../utils/callOrUse';
import { createHashedClassNameGetter } from '../../../../../../utils/cssModules';
import { useCurrentCategory } from '../../../../../CurrentCategoryProvider';
import { useUtils } from '../../../../../UtilsProvider';
import { useItemData } from '../../hooks/useItemData';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useHandleMouseEvents } from '../hooks/useHandleMouseEvents';
import { useItemStyle } from './hooks/useItemStyle';
import { useLiveTextStyle } from './hooks/useLiveTextStyle';
import { usePresetItemVideo } from './hooks/usePresetItemVideo';
import { useVideoSrcUrl } from './hooks/useVideoSrcUrl';
import styles from './PresetItem.scss';
import type { PresetItemProps } from './PresetItem.types';
import { isDebug } from '../../../../../../utils/env';

const getHashedClassName = createHashedClassNameGetter(styles);

export const PresetItem: FC<PresetItemProps> = ({
  item: _item,
  sectionImageRef,
  section,
  index: itemIndex,
  sectionIndex,
  isSelected,
  checkItemSelected,
  selectionIndicationSymbolName = '',
}) => {
  const { translate } = useUtils();

  const item = useItemData(_item);

  const {
    additionalBehaviours: { labelMode, iconEnabledForComps },
  } = section;
  const category = useCurrentCategory();
  const { id: categoryId } = category;

  const { systemId, id, preset, text, liveTextToShow, themeName, structure, overrideClass, className } = item;
  const { rect, labelClasses } = preset;

  const infoIcon = callOrUse(iconEnabledForComps[structure.componentType], structure.data);

  const { itemStyle, disableHover, enableHover } = useItemStyle({ item, section });

  const { hasVideo, isVideoPlaying, videoNodeRef, playVideo, pauseVideo } = usePresetItemVideo({ item, section });

  const {
    handleClick,
    handleMouseEnter: _handleMouseEnter,
    handleMouseLeave: _handleMouseLeave,
  } = useHandleMouseEvents({
    item,
    section,
    category,
    sectionIndex,
    itemIndex,
  });

  const handleMouseEnter = () => {
    _handleMouseEnter();

    if (hasVideo) {
      playVideo();
    } else {
      enableHover();
    }
  };

  const handleMouseLeave = () => {
    _handleMouseLeave();

    if (hasVideo) {
      pauseVideo();
    } else {
      disableHover();
    }
  };

  const videoSrc = useVideoSrcUrl(preset.video?.source);

  const liveTextStyle = useLiveTextStyle({ themeName });

  const { startItemDrag } = useDragAndDrop({ sectionImageRef });

  const handleMouseDown = (event: MouseEvent) =>
    startItemDrag(event, {
      categoryId,
      section,
      item,
      itemId: item.id,
      itemStructure: structure,
    });

  const debugProps = isDebug() && {
    'data-system-id': systemId,
  };

  const isSelectedItem = isSelected || checkItemSelected?.(item.structure);

  return (
    <div
      data-hook="preset-item"
      draggable={false}
      id={id}
      className={classnames(styles.presetItem, getHashedClassName(className), overrideClass)}
      data-comp={structure.componentType}
      style={itemStyle}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...debugProps}
    >
      {hasVideo && (
        <video
          id={id}
          ref={videoNodeRef}
          className={classnames(styles.video, !isVideoPlaying && styles.videoPaused)}
          width={rect.width}
          height={rect.height}
          loop
          src={videoSrc}
          preload="none"
        />
      )}

      {liveTextToShow && (
        <div data-hook="live-text">
          <span style={liveTextStyle}>{translate(liveTextToShow)}</span>
        </div>
      )}

      {labelMode !== LabelMode.None && preset.label && (
        <label
          className={classnames(
            styles.presetItemLabel,
            styles[labelMode],
            labelClasses && labelClasses.split(/\s+/).map(getHashedClassName),
          )}
        >
          {translate(preset.label)}
        </label>
      )}

      {infoIcon && (
        <div
          data-hook="info-icon-wrapper"
          className={classnames(styles.infoWrapper, labelMode !== LabelMode.None && styles.hasLabel)}
        >
          {/*<InfoIcon*/}
          {/*  width={CATEGORY_WRAPPER_WIDTH}*/}
          {/*  openHelp={dispatchHelpClick}*/}
          {/*  // @ts-expect-error*/}
          {/*  openHelp={console.log}*/}
          {/*  title={infoIcon.title}*/}
          {/*/>*/}
        </div>
      )}

      {isSelectedItem && <Symbol className={styles.selectionIndicator} name={selectionIndicationSymbolName} />}

      {text && (
        <div style={{ lineHeight: rect.height }}>
          <span>{translate(text)}</span>
        </div>
      )}
    </div>
  );
};
